import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FormHelperText, MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { assetApi } from '@application/api/endpoints/asset-api';
import { fetchAssets } from '@application/redux/Assets/asset-slice';
import SDatePicker from '@ui/components/common/SDatePicker';
import { convertToLocalTimezone } from '@application/utilities/date-time-formater';
import { ProjectApi } from '@application/api/endpoints/project-api';
// export const projects = [
//   { id: 'b94e0f61-3f23-45f1-b2de-0f1848d6e8a3', name: 'Project Alpha' },
//   { id: '7ac20a9e-81a4-4a8b-bf66-b4b79fffb201', name: 'Project Beta' },
//   { id: 'f4a11090-51fc-42b8-a0d9-619dd8a1995c', name: 'Project Gamma' },
//   { id: '1fe90b73-8f84-408a-9f72-77e60b22711b', name: 'Project Delta' },
//   { id: 'd1b58a56-8c0d-4023-9c35-17a7a09831f7', name: 'Project Epsilon' }
// ];
const initialFormData = {
    assetName: '',
    projectId: '',
    startDate: null,
    endDate: null,
    description: '',
    id: '',
    noOfSystems: 0
};
const AssetForm = ({ trigger, discardChanges, setDiscardChanges, setTrigger, onClose, assetId, goBack = false, onAssetChange, setHasDirtyValues, resetDialogOptions, setAllowWindowNavigation }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialFormData);
    const [initialAssetState, setInitialAssetState] = useState(initialFormData);
    const [project, setProject] = useState();
    const validateForm = useCallback((formData) => {
        const newErrors = {};
        let startDate = convertToLocalTimezone(formData.startDate);
        let endDate = convertToLocalTimezone(formData.endDate);
        if (!formData.assetName)
            newErrors.assetName = t('AssetNameRequired');
        if (formData.assetName.length > 50)
            newErrors.assetName = t('MaxLengthFifty');
        if (!formData.projectId)
            newErrors.project = t('ProjectRequired');
        if (!formData.startDate)
            newErrors.startDate = t('StartDateRequired');
        if (endDate && endDate < startDate) {
            newErrors.endDate = t('EndDateInvalid');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [t]);
    const [projectQuery, setProjectQuery] = useState({
        pageNumber: 1,
        pageSize: 1000
    });
    const handleSubmit = useCallback(async () => {
        if (!validateForm(formData)) {
            setTrigger(false);
            return;
        }
        const payload = {
            assetName: formData.assetName,
            projectId: formData.projectId,
            startDate: convertToLocalTimezone(formData.startDate),
            endDate: convertToLocalTimezone(formData.endDate),
            description: formData.description,
            id: formData.id,
            noOfSystems: formData.noOfSystems
        };
        try {
            const response = assetId
                ? await assetApi.updateAssetDetail(assetId, payload)
                : await assetApi.createAssetDetail(payload);
            if (response.isSuccess) {
                toast.success(response.message);
                setFormData(response.data);
                setInitialAssetState(response.data);
                await dispatch(fetchAssets());
            }
            else {
                toast.error(response.message);
            }
        }
        catch (error) {
            toast.error(t('SomethingWentWrong'));
        }
        finally {
            setTrigger(false);
            // Case: Add Asset
            if (!assetId) {
                setFormData(initialAssetState);
            }
            resetDialogOptions && resetDialogOptions();
            if (goBack && goBack === true) {
                navigate(`/detail-asset`);
            }
            if (onClose)
                onClose();
        }
    }, [
        validateForm,
        formData,
        assetId,
        dispatch,
        t,
        trigger,
        goBack,
        resetDialogOptions,
        onClose,
        initialAssetState,
        navigate
    ]);
    useEffect(() => {
        const getProjects = async () => {
            try {
                const response = await ProjectApi.getFilteredProjectDetail(projectQuery);
                if (response.isSuccess) {
                    setProject(response.data);
                }
            }
            catch (error) { }
        };
        getProjects();
    }, []);
    useEffect(() => {
        if (trigger) {
            handleSubmit();
        }
    }, [trigger]);
    useEffect(() => {
        if (discardChanges && setDiscardChanges) {
            setFormData(initialAssetState);
            setDiscardChanges(false);
            resetDialogOptions && resetDialogOptions();
            if (goBack && goBack === true) {
                navigate(`/detail-asset`);
            }
            onClose();
        }
    }, [discardChanges, initialAssetState, onClose, setDiscardChanges]);
    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialAssetState);
        setHasDirtyValues && setHasDirtyValues(hasChanges);
        setAllowWindowNavigation && setAllowWindowNavigation(hasChanges);
    }, [formData, initialAssetState]);
    useEffect(() => {
        if (assetId) {
            const getAssetById = async () => {
                try {
                    const response = await assetApi.getAssetDetailbyId(assetId);
                    if (response.isSuccess && response?.data) {
                        setFormData(response?.data);
                        setInitialAssetState(response?.data);
                        if (onAssetChange)
                            onAssetChange(response?.data?.assetName || '');
                    }
                }
                catch (error) { }
            };
            getAssetById();
        }
    }, [assetId]);
    // Setting Asset Name in Header on FormData change
    useEffect(() => {
        if (onAssetChange)
            onAssetChange(formData.assetName || '');
    }, [formData.assetName]);
    const handleInputChange = (field, value) => {
        setFormData((prev) => {
            const newFormData = { ...prev, [field]: value };
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (value) {
                    delete newErrors[field];
                }
                return newErrors;
            });
            return newFormData;
        });
    };
    return (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: 6, children: _jsx(TextField, { label: t('AssetName'), fullWidth: true, value: formData.assetName, onChange: (e) => handleInputChange('assetName', e.target.value), error: !!errors.assetName, helperText: errors.assetName }) }), _jsxs(Grid, { size: 6, children: [_jsx(TextField, { value: formData.projectId, onChange: (e) => handleInputChange('projectId', e.target.value), fullWidth: true, select: true, id: 'asset-project-dropdown', label: 'Project', "aria-placeholder": 'Project', error: !!errors.project, children: project?.data?.map((proj) => (_jsx(MenuItem, { value: proj.id, children: proj.projectName }, proj.id))) }), errors.project && _jsx(FormHelperText, { sx: { color: 'red' }, children: errors.project })] }), _jsx(Grid, { size: 6, children: _jsx(SDatePicker, { value: formData.startDate, onChange: (val) => handleInputChange('startDate', val), placeholder: 'Start Date', errormsg: errors.startDate }) }), _jsx(Grid, { size: 6, children: _jsx(SDatePicker, { value: formData.endDate, onChange: (val) => handleInputChange('endDate', val), placeholder: 'End Date', errormsg: errors.endDate }) }), _jsx(Grid, { size: 12, children: _jsx(TextField, { label: 'Description', value: formData.description, onChange: (e) => handleInputChange('description', e.target.value), multiline: true, rows: 4, fullWidth: true }) })] }));
};
export default AssetForm;
